/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable camelcase */

import { useEffect, FC, Fragment } from 'react';
import { useRouter } from 'next/router';
import { LearnHero } from 'components/02-molecules/LearnHero';
import { VideoPlayer } from 'components/01-atoms/VideoPlayer';
import { H1, H3 } from 'components/01-atoms/Heading';
import { Image } from 'components/01-atoms/Image';
import { MediaBlockText } from 'components/02-molecules/MediaBlockText';
import { MediaBlock } from 'components/02-molecules/MediaBlock';
import { RubricHeaderSection } from 'components/03-organisms/Section';
import { Carousel } from 'components/02-molecules/Carousel';
import { CourseCard } from 'components/02-molecules/CourseCard';
import { CallToAction } from 'components/02-molecules/CallToAction';
import { useTailwindBreakpoint } from 'hooks/useTailwindBreakpoint';
import TableOfContents from 'components/03-organisms/TableOfContents';
import { useArticleTextAsJsx } from 'hooks/useArticleTextAsJsx';
import {
  useUserType,
  joinOutside,
  useIsOutsideDigitalUser
} from 'components/04-templates';
import { useAnalyticsContext } from 'components/04-templates/AnalyticsProvider';
import { routes } from 'lib/routes';
import {
  OUTSIDE_ONLINE_URLS,
  PLACEHOLDER_IMAGE,
  FASTLY_PARAMS,
  APPLICATION_URL
} from 'lib/constants';
import type { LinkConfig } from 'types/WebMicrofrontends';
import { GET_COURSE_INTRODUCTION } from 'queries';
import CourseWrapper from 'components/04-templates/CourseWrapper';
import {
  CourseIntroductionPage,
  Instructor,
  Lesson,
  MediaBlockData,
  TableOfContentsSectionData
} from 'types';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { parsePageSeo } from 'utils';
import { initializeApollo, useCache } from 'helpers/hooks';
import Head from 'next/head';
import { useApolloClient } from '@apollo/client';
import { Icon } from 'components/01-atoms/Icon';
import { openUpgradeOfferPianoModal } from 'lib/piano/helpers';

const CourseIntroduction: FC<CourseIntroductionPage> = ({
  props,
  variables
}) => {
  const router = useRouter();
  const { isScreenWiderOrEqualTo, isScreenNarrowerThan } =
    useTailwindBreakpoint();
  const [lessonText, setArticleText] = useArticleTextAsJsx('');
  const { isOutsidePlusUser } = useUserType();

  const isOutsideDigitalUser = useIsOutsideDigitalUser();

  const { elementClicked, options } = useAnalyticsContext();

  const {
    query: { courseName },
    asPath
  } = router;

  // this gives us the same Apollo Client that was passed into the ApolloProvider in LearnFederationProvider.tsx
  const client = useApolloClient();

  useEffect(() => {
    client.writeQuery({
      query: GET_COURSE_INTRODUCTION,
      variables,
      data: {
        props
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseName]);

  const name = courseName as string;

  const {
    cta,
    paywall,
    brand,
    coverage,
    curriculum,
    hero,
    heroImage,
    instructors,
    mediaBlocks,
    overview,
    courseSeo,
    tableOfContents,
    courseTitle
  } = useCache({ ...variables, getCourseIntro: true });

  const lessons = tableOfContents
    ?.map((section: TableOfContentsSectionData) => section.lessons)
    .flat();

  const firstLessonName = lessons?.[0]?.name;
  const relativePath = `/${courseName}/${firstLessonName}`;

  const handleGetStartedButtonClick = () => {
    elementClicked({
      name: 'initiated-course',
      type: 'link',
      destination_url: `${options.baseUrl}${relativePath}`
    });
  };

  const handleGetStarteKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleGetStartedButtonClick();
    }
  };

  const CTAButtonConfigOnClick = isOutsideDigitalUser
    ? openUpgradeOfferPianoModal
    : joinOutside;

  const CTAButtonConfig: LinkConfig = {
    ...(!isOutsidePlusUser
      ? { onClick: CTAButtonConfigOnClick }
      : { href: OUTSIDE_ONLINE_URLS.BILLING }),
    text: cta?.buttonText,
    target: '_self'
  };

  useEffect(() => {
    // Set parsed content
    if (overview?.description) {
      setArticleText(overview.description);
    }
  }, [overview, setArticleText]);

  const schemaData = [
    {
      '@context': 'https://schema.org/',
      '@type': 'Course',
      '@id': `${APPLICATION_URL}${asPath.split('?')[0]}`,
      url: `${APPLICATION_URL}${asPath.split('?')[0]}`,
      name: courseTitle,
      description: courseSeo?.description || overview?.description,
      isAccessibleForFree: 'False',
      provider: {
        '@type': 'Organization',
        name: 'Outside',
        url: 'https://www.outsideonline.com/'
      },
      hasPart: lessons?.map((lesson) => ({
        '@context': 'https://schema.org/',
        '@type': 'LearningResource',
        name: lesson?.title,
        url: `${APPLICATION_URL}${routes.lessonDetail(name, lesson.name)}`,
        '@id': `${APPLICATION_URL}${routes.lessonDetail(name, lesson.name)}`,
        isAccessibleForFree: 'False',
        provider: {
          '@type': 'Organization',
          name: 'Outside',
          url: 'https://www.outsideonline.com/'
        }
      }))
    }
  ];

  return (
    <>
      {courseSeo && <NextSeo {...parsePageSeo({ ...courseSeo, asPath })} />}
      <Head>
        <script
          type="application/ld+json"
          id={`learn-course-${name}-schema`}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
          key={`learn-course-${name}-schema`}
        />
      </Head>
      <CourseWrapper toc={tableOfContents}>
        <LearnHero
          headingText={courseTitle}
          dekText={hero?.subhead}
          logoSource={brand?.image || ''}
          video={
            hero?.video && (
              <VideoPlayer
                playerId={hero.video}
                playerScript="https://cdn.jwplayer.com/libraries/x56QiR8i.js"
                playlist={`https://content.jwplatform.com/feeds/${hero.video}.json`}
                classes="aspect-w-16 aspect-h-9 bg-network-black-soft"
              />
            )
          }
          image={
            heroImage && (
              <Image
                alt={`${courseTitle} hero image`}
                classes="h-auto w-full"
                src={`${heroImage}?${FASTLY_PARAMS}`}
              />
            )
          }
        />

        {isScreenNarrowerThan('wide') && (
          <TableOfContents
            toc={tableOfContents}
            classes="w-full"
            allowOpenClose
          />
        )}

        <section>
          <div
            id="course-body"
            className="grid grid-cols-12 pb-4 narrow-x:gap-7"
          >
            {!isOutsidePlusUser ? (
              <div
                id="cta-container"
                className="col-span-12 w-full px-2 pb-4 narrow-x:order-last narrow-x:col-span-5 narrow-x:px-0 narrow-x:pt-6"
              >
                <CallToAction
                  isFullWidth={isScreenNarrowerThan('narrow')}
                  color="dark"
                  heading={
                    <H3
                      classes="u-text--uppercase-xl text-network-white tracking-wide-xxx narrow-x:text-26 leading-theme-height-uppercase-xl"
                      headingText={cta?.headline}
                    />
                  }
                  dek={cta?.description}
                  linkConfig={CTAButtonConfig}
                  price={paywall?.price}
                  priceDuration={paywall?.priceDuration}
                  priceDisclaimer={cta?.priceDisclaimer}
                  discountPrice={paywall?.discountPrice}
                  discountCode={paywall?.discountCode}
                  classes="mt-12 wide:mt-0 grid gap-4"
                  buttonClasses="narrow-x:w-[250px]"
                />
              </div>
            ) : (
              <div
                id="get-started-button-container"
                className="col-span-12 w-full px-9 pt-10 narrow-x:order-last narrow-x:col-span-5 narrow-x:px-0 narrow-x:pt-8"
              >
                <Link href={relativePath} passHref>
                  <a
                    role="link"
                    tabIndex={0}
                    onClick={handleGetStartedButtonClick}
                    onKeyPress={handleGetStarteKeyPress}
                  >
                    <div className="btn-secondary btn-lg flex w-full items-center justify-center">
                      <Icon type="play" color="white" size="small-plus" />
                      Get Started
                    </div>
                  </a>
                </Link>
              </div>
            )}

            <div
              id="overview-content"
              className="col-span-12 narrow-x:col-span-7 "
            >
              <RubricHeaderSection headingText="Overview">
                <div className="px-9 narrow-x:pl-12">
                  <H1 headingText={overview?.title} classes="mb-6" />
                  <div className="u-link--brick whitespace-pre-wrap">
                    {lessonText}
                  </div>
                </div>
              </RubricHeaderSection>
            </div>
          </div>
        </section>
        {coverage?.title && (
          <section className="u-grid--std-12 flex">
            <MediaBlock
              classes="mb-7 narrow-x:mb-0 narrow-x:pl-12"
              textConfig={{
                headerComponent: <H3 headingText={coverage?.title} />,
                bulletedText: coverage?.text,
                classes: `px-9 narrow-x:px-0 u-link--brick ${
                  coverage?.imageAttachment?.file ? '' : 'narrow-x:-ml-6'
                }`
              }}
              imageConfig={{
                src: coverage?.imageAttachment?.file,
                alt: coverage?.imageAttachment?.altText
              }}
              imagePriority
              alignImage={coverage?.imageAttachment?.file ? 'left' : 'right'}
            />
          </section>
        )}
        {curriculum?.lessons.length > 0 && (
          <RubricHeaderSection headingText="Featured Lessons">
            <Carousel viewLimit={3} mobileViewLimit={1}>
              {(curriculum.lessons || []).map((lesson: Lesson) => (
                <Fragment key={lesson.id}>
                  <CourseCard
                    imageSrc={
                      lesson.image
                        ? `${lesson.image}?crop=16:9&width=450&${FASTLY_PARAMS}`
                        : PLACEHOLDER_IMAGE
                    }
                    imageAlt={lesson.title}
                    heading={lesson.title}
                    description={lesson.description}
                    href={routes.lessonDetail(name, lesson.name)}
                    // TODO - display multiple instructors, if needed
                    instructor={lesson.instructors?.[0]?.name}
                    hoverOverlayText="Go to Lesson"
                  />
                </Fragment>
              ))}
            </Carousel>
          </RubricHeaderSection>
        )}

        {instructors?.length > 0 && (
          <RubricHeaderSection
            headingText={`Instructor${instructors.length > 1 ? 's' : ''}`}
          >
            <div className="u-grid--std-12 mb-2">
              {instructors.map((instructor: Instructor, i) => (
                <Fragment key={`${instructor.name + i}`}>
                  <div className="col-span-3 px-24 narrow-x:pl-12 narrow-x:pr-7 ">
                    {instructor.profilePicture && (
                      <div className="max-w-[300px] overflow-hidden rounded-full">
                        <Image
                          classes=""
                          alt={instructor.name}
                          src={`${instructor.profilePicture}?crop=1:1&width=300&${FASTLY_PARAMS}`}
                        />
                      </div>
                    )}
                  </div>

                  <div className="u-link--brick col-span-9 px-9 narrow-x:pl-12">
                    <MediaBlockText
                      headerComponent={
                        <H3 headingText={instructor.name} className="h3" />
                      }
                      richText={instructor.bio}
                    />
                  </div>
                </Fragment>
              ))}
            </div>
          </RubricHeaderSection>
        )}
        {(mediaBlocks || []).map((mediaBlock: MediaBlockData, i) => (
          <Fragment key={`${mediaBlock.title + i}`}>
            <RubricHeaderSection headingText={mediaBlock.title}>
              <div className="u-grid--std-12 mb-2">
                {mediaBlock.image.file && (
                  <div className="col-span-1 px-24 narrow-x:col-span-6 narrow-x:pl-12 narrow-x:pr-7 ">
                    <Image
                      classes="w-full h-auto"
                      alt={mediaBlock.title}
                      src={`${mediaBlock.image.file}?crop=16:9&width=1250`}
                      sizes="(max-width: 600px), (min-width: 601px)"
                      srcSet={`${mediaBlock.image.file}?crop=600:340&width=600, ${mediaBlock.image.file}?crop=16:9&width=1250`}
                    />
                  </div>
                )}
                <div
                  className={`col-span-1  px-9 narrow-x:pl-12 ${
                    mediaBlock.image.file
                      ? 'narrow-x:col-span-6'
                      : 'narrow-x:col-span-12'
                  }`}
                >
                  <MediaBlockText
                    // TO-DO REMOVE THIS HEADER WHEN IT IS NO LONGER REQUIRED BY MEDIABLOCKTEXT!!! ALSO
                    // ADD IN o-rich-text to classes in richText in component.
                    headerComponent={<H3 headingText="" className="h3" />}
                    richText={mediaBlock.description}
                    classes="u-link--brick"
                  />
                </div>
              </div>
            </RubricHeaderSection>
          </Fragment>
        ))}

        {!isOutsidePlusUser && (
          <div className="px-2 wide:px-0">
            <CallToAction
              isFullWidth={isScreenWiderOrEqualTo('narrow-x')}
              color="dark"
              heading={
                <H3
                  classes="u-text--uppercase-xl text-network-white narrow-x:text-20"
                  headingText={cta?.headline}
                />
              }
              dek={cta?.description}
              linkConfig={CTAButtonConfig}
              price={paywall?.price}
              priceDuration={paywall?.priceDuration}
              priceDisclaimer={cta?.priceDisclaimer}
              discountPrice={paywall?.discountPrice}
              discountCode={paywall?.discountCode}
              classes="my-8"
            />
          </div>
        )}
      </CourseWrapper>
    </>
  );
};

export async function getServerSideProps({
  req,
  params,
  previewData,
  preview = null
}) {
  const { courseName } = params;
  const { thumbnailId, courseId } = previewData?.page || {};
  const { cookies } = req || {};
  const {
    outside_access: outsideAccess,
    _r_token: rivtToken,
    _pid: pianoToken
  } = cookies;

  const apolloClient = initializeApollo({
    outsideAccess,
    rivtToken,
    pianoToken
  });

  const variables = {
    courseName,
    courseId: courseId || null,
    preview,
    thumbnailId: thumbnailId || null,
    relatedCoursesCount: 3
  };

  const { data, errors } = await apolloClient.query<CourseIntroductionPage>({
    query: GET_COURSE_INTRODUCTION,
    errorPolicy: 'all',
    variables,
    fetchPolicy: 'no-cache'
  });

  if (errors) {
    return {
      redirect: {
        destination: '/404',
        permanent: false
      }
    };
  }

  const { props } = data;

  return {
    props: {
      props,
      variables
    }
  };
}

export default CourseIntroduction;
